/**
 * Data layer for `self-signup-manual-approval-for-advertiser-accounts`. Thin typed fetch wrappers over /api/v1/self-signup-manual-approval-for-advertiser-accounts.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface SelfSignupManualApprovalForAdvertiserAccountsRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListSelfSignupManualApprovalForAdvertiserAccountsResponse {
	count: number;
	items: SelfSignupManualApprovalForAdvertiserAccountsRow[];
}

export interface CreateSelfSignupManualApprovalForAdvertiserAccountsBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/self-signup-manual-approval-for-advertiser-accounts";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListSelfSignupManualApprovalForAdvertiserAccountsResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListSelfSignupManualApprovalForAdvertiserAccountsResponse>(res);
}

export async function create(body: CreateSelfSignupManualApprovalForAdvertiserAccountsBody): Promise<SelfSignupManualApprovalForAdvertiserAccountsRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<SelfSignupManualApprovalForAdvertiserAccountsRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const selfSignupManualApprovalForAdvertiserAccountsQueryKeys = {
	all: ["self-signup-manual-approval-for-advertiser-accounts"] as const,
	list: () => [...selfSignupManualApprovalForAdvertiserAccountsQueryKeys.all] as const,
	detail: (id: string) => [...selfSignupManualApprovalForAdvertiserAccountsQueryKeys.all, id] as const,
};
